<!-- filepath: d:\programming\vuejs-components\Vc Website Components\src\components\DownloadPreviewer.vue -->
<template>
  <div class="position-relative ">
    <!--
    <div class="position-absolute top-0 end-50 w-100 h-100 fs-xs text-end text-muted pe-2 pt-1" style="pointer-events: none; user-select: none; z-index: 1;">
         {{  containerWidth }} x {{  containerHeight }}
    </div>-->
    <!-- Debounce Warning -->
    <div v-if="pendingRefresh" class="alert alert-warning d-flex align-items-center mb-3" role="alert">
      <div class="spinner-border spinner-border-sm me-2" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <div class="me-auto">
        Refreshing in {{ timeRemaining }} seconds...
        <small class="d-block text-muted">To prevent excessive API calls, updates are delayed.</small>
      </div>
      <!--
      <button type="button" class="btn btn-sm btn-warning" @click="refreshNow">
        <i class="bi bi-arrow-clockwise"></i> Refresh Now
      </button>
    -->
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage && !pendingRefresh" class="alert alert-danger my-3" role="alert">
      <i class="bi bi-exclamation-diamond"></i> {{ errorMessage }}
    </div>


    <!-- Loading Spinner -->
    <div v-if="isRefreshing && !pendingRefresh" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Data Display -->
     <template v-if="resultData && !isRefreshing && !pendingRefresh">
      
        <!-- Mobile View - Show expand link on small screens -->
        <div v-if="!isMaximized && shouldShowCompactView" class="text-center py-2 bg-light rounded">
          <button 
            type="button" 
            class="btn btn-link text-decoration-none"
            @click="toggleMaximize">
            <i class="bi bi-arrows-angle-expand me-1"></i>
            Expand for format preview
          </button>
          <!--<i class="bi bi-eye text-primary mb-2 d-block" style="font-size: 2rem;"></i>
          <button 
            type="button" 
            class="btn btn-link text-decoration-none"
            @click="toggleMaximize">
            <i class="bi bi-arrows-angle-expand me-1"></i>
            Expand for format preview
          </button>
          <div class="text-muted small mt-1">
            {{ isCSV ? 'CSV' : 'JSON' }} data ready
          </div>
          -->
        </div>
        
        <!-- Desktop View or Maximized - Show full preview -->
        <div v-else class="preview-wrapper position-relative" :style="previewWrapperStyle">
          <!-- Maximize/Minimize button -->
          <button 
            type="button" 
            class="btn btn-sm btn-primary position-absolute top-0 end-0 m-1 me-3" 
            style="z-index: 10;"
            @click="toggleMaximize"
            :title="isMaximized ? 'Minimize' : 'Maximize'">
            <i :class="isMaximized ? 'bi bi-arrows-angle-contract' : 'bi bi-arrows-angle-expand'"></i>
          </button>
          <!--Don't add formatting because you see it in the output :)-->
          <pre class="preview-pre" :class="isCSV ? 'language-csv' : 'language-json'"><code :class="isCSV ? 'language-csv' : 'language-json'" v-html="highlightedCode"></code></pre>
        </div>
        
        <!-- Cost Saver Mode Warning - Show on desktop or when maximized -->
        <div v-if="(costSaverMode && !pendingRefresh) && (!shouldShowCompactView || isMaximized)" 
            class="text-muted small mt-2">
          <i class="bi bi-info-circle me-1"></i> Preview mode. The data may be truncated.
        </div>

      </template>
  </div>
</template>

<script>
import { VcListUtils } from '@/visualcrossing-core/vclistutils';
import { parse } from 'csv-parse/browser/esm';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-csv';
import 'prismjs/components/prism-json';

export default {
  name: 'DownloadPreviewer',
  props: {
    queryDefinition: Object,
    sessionState: Object,
    location: Object,
    costSaverMode: {
      type: Boolean,
      default: false
    },
    debounceDelay: {
      type: Number,
      default: 3000 // 3 seconds default
    },
    cacheExpiry: {
      type: Number,
      default: 900000 // 5 minutes default (in milliseconds)
    },
    maximizeTarget: {
      type: String,
      default: null // CSS selector for the parent element to maximize into
    }
  },
  
  data() {
    return {
      windowWidth: window.innerWidth,
      mdBreakpoint: 1000, // Bootstrap's md breakpoint
      containerWidth: null, // Track actual container width
      containerHeight: null, // Track actual container height
      resizeObserver: null, // ResizeObserver instance
      resultData: null,
      errorMessage: null,
      isRefreshing: false,
      resolvedQueryDefinition: null,
      rawRecords: null,
      parsedJson: null,
      pendingRefresh: false,
      debounceTimer: null,
      countdownTimer: null,
      timeRemaining: 0,
      lastRequestTime: null,
      requestCache: new Map(),
      isMaximized: false,
      originalStyles: null,
      maximizedElement: null,
      // New: Configurable thresholds
      minWidthForPreview: 500, // Minimum width to show preview
      minHeightForPreview: 175 // Minimum height to show preview
    };
  },
  
  methods: {
      
    setupResizeObserver() {
      // Clean up existing observer if any
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      
      // Find the target element to observe
      let targetElement = null;

      if (!targetElement) {
        // Fall back to the component's parent element
        targetElement = this.$el; 
      }
      
      if (!targetElement) {
        console.warn('No element to observe for resize');
        return;
      }
      
      // Create ResizeObserver
      this.resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          // Get the content box size (excludes padding and border)
          const { width, height } = entry.contentRect;
          
          // Update tracked dimensions
          this.containerWidth = Math.round(width);
          this.containerHeight = Math.round(height);
          
          // Log for debugging
          //console.log(`Container resized: ${width}x${height}px`);
          
          // Optional: Emit event for parent components
          this.$emit('container-resized', { width, height });
        }
      });
      
      // Start observing
      this.resizeObserver.observe(targetElement);
      
      // Get initial size
      const rect = targetElement.getBoundingClientRect();
      this.containerWidth = rect.width;
      this.containerHeight = rect.height;
    },
    // Generate cache key from request parameters
    getCacheKey(queryDefn, location, name, contentType) {
      const key = queryDefn.include + '_' + contentType;
      return key;
      /*
      const keyObj = {
        include: queryDefn.include || '',
        options: queryDefn.options || '',
        location: location || '',
        name: name || '',
        contentType: contentType || 'json',
      };
      return JSON.stringify(keyObj);
      */
    },
    
    // Check if cache entry is still valid
    isCacheValid(cacheEntry) {
      if (!cacheEntry) return false;
      const now = Date.now();
      return (now - cacheEntry.timestamp) < this.cacheExpiry;
    },
    
    // Get from cache
    getFromCache(key) {

      const cacheEntry = this.requestCache.get(key);
      if (cacheEntry && this.isCacheValid(cacheEntry)) {
        //console.log('Using cached data for key:', key, cacheEntry.data?.resultData, cacheEntry.data?.rawRecords);
        return cacheEntry;
      }
      // Remove expired entry
      if (cacheEntry) {
        this.requestCache.delete(key);
      }
      return null;
    },
    
    // Save to cache
    saveToCache(key, data, error = null) {
      const cacheEntry = {
        data: data,
        error: error,
        timestamp: Date.now()
      };
      this.requestCache.set(key, cacheEntry);
      console.log('Saved to cache for key:', key);
      
      // Limit cache size to prevent memory issues
      if (this.requestCache.size > 50) {
        // Remove oldest entries
        const entries = Array.from(this.requestCache.entries());
        entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
        for (let i = 0; i < 10; i++) {
          this.requestCache.delete(entries[i][0]);
        }
      }
    },
    
    // Clear cache
    clearCache() {
      this.requestCache.clear();
      console.log('Cache cleared');
    },
    
    // Clear expired cache entries
    cleanupCache() {
      const now = Date.now();
      const keysToDelete = [];
      
      this.requestCache.forEach((entry, key) => {
        if ((now - entry.timestamp) >= this.cacheExpiry) {
          keysToDelete.push(key);
        }
      });
      
      keysToDelete.forEach(key => this.requestCache.delete(key));
      
      if (keysToDelete.length > 0) {
        console.log(`Cleaned up ${keysToDelete.length} expired cache entries`);
      }
    },
    
    highlightCode() {
      this.$nextTick(() => {
        if (typeof Prism !== 'undefined') {
          const codeBlock = this.$refs.codeBlock;
          if (codeBlock) {
            // Prism will work on any element with the language class
            //Prism.highlightElement(codeBlock);
          }
        }
      });
    },
    
    createResolvedQueryDefinition() {
      const me = this;
      if (!me.queryDefinition || !me.location || !me.queryDefinition.goal) {
         console.log('No valid queryDefinition or location - clearing resolvedQueryDefinition');
        me.resolvedQueryDefinition = null;
        return;
      }
      me.resolvedQueryDefinition = JSON.parse(JSON.stringify(me.queryDefinition));
      
      // Set appropriate include if not set
      if (me.isCSV) {
        const csvInclude = VcListUtils.csvInclude(me.resolvedQueryDefinition.include);
        if (!csvInclude) {
          me.resolvedQueryDefinition.include = VcListUtils.setValueInList(
            me.resolvedQueryDefinition.include, 
            "days", 
            true, 
            false
          );
        }
      }
    },
    
    scheduleRefresh() {
      const me = this;
      
      // Make sure we have a valid query definition first
      if (!me.resolvedQueryDefinition) {
        console.log('No resolved query definition yet');
        return;
      }
      
      // Get the actual content type that will be used
      let actualContentType = me.resolvedQueryDefinition?.contentType || 'json';
      if (actualContentType === 'xlsx') {
        actualContentType = 'csv';
      }
      
      // Check cache with the ACTUAL content type
      const cacheKey = me.getCacheKey(
        me.resolvedQueryDefinition,
        me.location?.location,
        me.location?.name,
        actualContentType  // Use the actual content type, not from resolvedQueryDefinition
      );
      
      console.log('scheduleRefresh - Cache key:', cacheKey);
      
      const cachedData = me.getFromCache(cacheKey);
      if (cachedData) {
        console.log('Found cached data in scheduleRefresh');
        // Use cached data immediately
        me.processCachedData(cachedData);
        return;
      }

      console.log(`scheduleRefresh ${me.isCSV ? 'CSV' : 'JSON'} data - no cache found`);
      
      // Clear existing timers
      if (me.debounceTimer) {
        clearTimeout(me.debounceTimer);
      }
      if (me.countdownTimer) {
        clearInterval(me.countdownTimer);
      }
      
      // Calculate time since last request
      const now = Date.now();
      const timeSinceLastRequest = me.lastRequestTime ? now - me.lastRequestTime : Infinity;
      
      // If enough time has passed, refresh immediately
      if (timeSinceLastRequest >= me.debounceDelay) {
        me.refreshData();
        return;
      }
      
      // Otherwise, schedule refresh with remaining time
      const remainingDelay = me.debounceDelay - timeSinceLastRequest;
      
      // Set pending state
      me.pendingRefresh = true;
      me.timeRemaining = Math.ceil(remainingDelay / 1000);
      
      // Start countdown
      me.countdownTimer = setInterval(() => {
        me.timeRemaining--;
        if (me.timeRemaining <= 0) {
          clearInterval(me.countdownTimer);
        }
      }, 1000);
      
      // Schedule the actual refresh
      me.debounceTimer = setTimeout(() => {
        me.pendingRefresh = false;
        me.refreshData();
      }, remainingDelay);
    },
    
    processCachedData(cacheEntry) {
      const me = this;
      
      if (cacheEntry.error) {
        me.errorMessage = cacheEntry.error;
        me.resultData = null;
        me.rawRecords = null;
        me.parsedJson = null;
      } else {
        me.resultData = cacheEntry.data.resultData;
        me.rawRecords = cacheEntry.data.rawRecords;
        me.parsedJson = cacheEntry.data.parsedJson;
        me.errorMessage = null;
        
        // Re-highlight code
        me.highlightCode();
      }
    },
    
    refreshNow() {
      const me = this;
      
      // Clear timers
      if (me.debounceTimer) {
        clearTimeout(me.debounceTimer);
      }
      if (me.countdownTimer) {
        clearInterval(me.countdownTimer);
      }
      
      me.pendingRefresh = false;
      
      // Force refresh (bypass cache)
      me.refreshData(true);
    },
    
    refreshData(forceRefresh = false) {
      const me = this;
      console.log(`refreshData ${me.isCSV ? 'CSV' : 'JSON'} data`);
      
      // Track request time
      me.lastRequestTime = Date.now();
      const retrieveFullData = !me.costSaverMode && me.sessionState;
      me.errorMessage = null;
      
      if (!me.location || !me.location.location || !me.resolvedQueryDefinition) return;
      
      const retrievalQueryDefn = JSON.parse(JSON.stringify(me.resolvedQueryDefinition));
      
      // IMPORTANT: Set options BEFORE generating cache key
      if (!retrieveFullData) {
        retrievalQueryDefn.options = VcListUtils.setValueInList(
          retrievalQueryDefn.options,
          "preview",
          true,
          false
        );
      }
      
      let contentType = me.resolvedQueryDefinition.contentType || 'json';
      if (contentType === 'xlsx') {
        contentType = 'csv';
      }
      
      // NOW generate cache key with the modified retrievalQueryDefn
      const cacheKey = me.getCacheKey(
        retrievalQueryDefn,  // This now includes the preview option
        me.location.location,
        me.location.name,
        contentType
      );
      
      console.log('refreshData - Cache key:', cacheKey);
      
      // Check cache unless force refresh
      if (!forceRefresh) {
        const cachedData = me.getFromCache(cacheKey);
        if (cachedData) {
          console.log('Using cached data instead of API call');
          me.processCachedData(cachedData);
          return;
        }
      }
      
      // Clear data when making new request
      me.isRefreshing = true;
      me.resultData = null;
      me.rawRecords = null;
      me.parsedJson = null;
      
      if (!retrieveFullData) {
        retrievalQueryDefn.options = VcListUtils.setValueInList(
          retrievalQueryDefn.options,
          "preview",
          true,
          false
        );
        
        if (window["gtag"]) {
          gtag('event', `data_${me.isCSV ? 'csv' : 'json'}_preview`, { method: 'Vc' });
        }
      } else {
        if (window["gtag"]) {
          gtag('event', `data_${me.isCSV ? 'csv' : 'json'}_full`, { method: 'Vc' });
        }
      }
      
      vcCommunicator.retrieveTimelineData(
        retrievalQueryDefn,
        me.location.location,
        me.location.name,
        contentType,
        true,
        false
      )
      .then(response => {
        me.resultData = response;
        me.isRefreshing = false;
        
        if (me.isCSV) {
          // Parse CSV data
          parse(response, {
            relax_column_count: true
          }, function(err, records) {
            if (err) {
              me.errorMessage = err.message || err;
              // Cache the error
              me.saveToCache(cacheKey, null, me.errorMessage);
            } else {
              me.rawRecords = records;
              // Cache the successful result
              me.saveToCache(cacheKey, {
                resultData: me.resultData,
                rawRecords: me.rawRecords,
                parsedJson: null
              });
            }
          });
        } else {
          // Parse JSON data
          try {
            me.parsedJson = typeof response === 'string' ? JSON.parse(response) : response;
            // Cache the successful result
            me.saveToCache(cacheKey, {
              resultData: me.resultData,
              rawRecords: null,
              parsedJson: me.parsedJson
            });
          } catch (e) {
            me.errorMessage = 'Invalid JSON response';
            // Cache the error
            me.saveToCache(cacheKey, null, me.errorMessage);
          }
        }
        
        me.highlightCode();
      })
      .catch((error) => {
        me.resultData = null;
        me.rawRecords = null;
        me.parsedJson = null;
        me.errorMessage = error.message || error;
        me.isRefreshing = false;
        
        // Cache the error
        me.saveToCache(cacheKey, null, me.errorMessage);
      });
    },
    
    copyToClipboard() {
      const text = this.formattedResultData;
      navigator.clipboard.writeText(text).then(() => {
        // Could add a toast notification here
        console.log('Copied to clipboard');
      });
    },
    
    toggleMaximize() {
      if (this.isMaximized) {
        this.minimize();
      } else {
        this.maximize();
      }
    },
    maximize() {
      this.isMaximized = true;
      
      // Emit event for parent to handle
      this.$emit('maximize');
      
      // Option 1: If we have a maximize target, use it
      if (this.maximizeTarget) {
        this.$nextTick(() => {
          const targetElement = document.querySelector(this.maximizeTarget);
          if (targetElement) {
            console.log('Maximizing into target element:', this.maximizeTarget);
            this.maximizeIntoElement(targetElement);
          } else {
            console.warn('Maximize target element not found:', this.maximizeTarget);
            // Fallback to fullscreen overlay
            this.createFullscreenOverlay();
          }
        });
      } 
      // Option 2: Create fullscreen overlay
      else {
        this.createFullscreenOverlay();
      }
    },
    
    minimize() {
      this.isMaximized = false;
      
      // Emit event for parent
      this.$emit('minimize');
      
      // Remove maximized element
      if (this.maximizedElement) {
        // If it's the in-element maximized container
        if (this.maximizedElement.classList.contains('download-preview-maximized-container')) {
          this.maximizedElement.remove();
          
          // Restore original position if we changed it
          if (this.restoredPosition && this.targetElement) {
            this.targetElement.style.position = '';
            this.restoredPosition = false;
          }
        }
        // If it's the fullscreen overlay
        else if (this.maximizedElement.classList.contains('download-preview-fullscreen')) {
          this.maximizedElement.remove();
        }
        
        this.maximizedElement = null;
      }
      
      // Remove ESC handler
      if (this.escHandler) {
        document.removeEventListener('keydown', this.escHandler);
        this.escHandler = null;
      }
      
      this.targetElement = null;
    },
    
    maximizeIntoElement(targetElement) {
      // Create a maximized container within the target element
      const maximizedContainer = document.createElement('div');
      maximizedContainer.className = 'download-preview-maximized-container';
      maximizedContainer.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: white;
        z-index: 1000;
        display: flex;
        flex-direction: column;
        animation: fadeIn 0.2s ease-in;
      `;
      
      // Create header
      const header = document.createElement('div');
      header.className = 'maximized-header';
      header.style.cssText = `
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem;
        border-bottom: 1px solid #dee2e6;
        background: #f8f9fa;
      `;
      header.innerHTML = `
        <h6 class="mb-0">${this.isCSV ? 'CSV' : 'JSON'} Preview - Full View</h6>
        <button type="button" class="btn btn-sm btn-secondary">
          <i class="bi bi-arrows-angle-contract"></i> Minimize
        </button>
      `;
      
      // Create body container
      const body = document.createElement('div');
      body.className = 'maximized-body';
      body.style.cssText = `
        flex: 1;
        overflow: auto;
        padding: 1rem;
        min-height: 0;
      `;
      
      // Clone the preview content
      const previewContent = this.$el.querySelector('.preview-pre');
      if (previewContent) {
        const clonedContent = previewContent.cloneNode(true);
        // Remove the fixed height constraint for maximized view
        clonedContent.style.position = 'static';
        clonedContent.style.height = 'auto';
        clonedContent.style.maxHeight = 'none';
        body.appendChild(clonedContent);
      }
      
      // Assemble the container
      maximizedContainer.appendChild(header);
      maximizedContainer.appendChild(body);
      
      // Add close handler
      header.querySelector('button').addEventListener('click', () => {
        this.minimize();
      });
      
      // Add ESC key handler
      this.escHandler = (e) => {
        if (e.key === 'Escape') {
          this.minimize();
        }
      };
      document.addEventListener('keydown', this.escHandler);
      
      // Store reference and add to target
      this.maximizedElement = maximizedContainer;
      this.targetElement = targetElement;
      
      // Store original position style if needed
      const originalPosition = window.getComputedStyle(targetElement).position;
      if (originalPosition === 'static') {
        targetElement.style.position = 'relative';
        this.restoredPosition = true;
      }
      
      // Append to target element
      targetElement.appendChild(maximizedContainer);
      
      // Re-run syntax highlighting on the cloned content
      this.$nextTick(() => {
        if (typeof Prism !== 'undefined') {
          Prism.highlightAllUnder(maximizedContainer);
        }
      });
    },
    
    createFullscreenOverlay() {
      // Create a fullscreen modal-like overlay
      const overlay = document.createElement('div');
      overlay.className = 'download-preview-fullscreen';
      overlay.innerHTML = `
        <div class="fullscreen-content">
          <div class="fullscreen-header">
            <h5>${this.isCSV ? 'CSV' : 'JSON'} Preview</h5>
            <button type="button" class="btn-close btn-close-white" aria-label="Close"></button>
          </div>
          <div class="fullscreen-body"></div>
        </div>
      `;
      
      // Clone the preview content
      const previewContent = this.$el.querySelector('.preview-pre').cloneNode(true);
      overlay.querySelector('.fullscreen-body').appendChild(previewContent);
      
      // Add close handler
      overlay.querySelector('.btn-close').addEventListener('click', () => {
        this.minimize();
        overlay.remove();
      });
      
      // Add ESC key handler
      const escHandler = (e) => {
        if (e.key === 'Escape') {
          this.minimize();
          overlay.remove();
          document.removeEventListener('keydown', escHandler);
        }
      };
      document.addEventListener('keydown', escHandler);
      
      // Append to body
      document.body.appendChild(overlay);
      this.maximizedElement = overlay;
    },
    
    restoreFromMaximized() {
      if (this.originalStyles) {
        const previewWrapper = this.$el.querySelector('.preview-wrapper');
        if (previewWrapper) {
          Object.assign(previewWrapper.style, this.originalStyles);
        }
        this.originalStyles = null;
      }
      
      if (this.maximizedElement && this.maximizedElement.classList.contains('download-preview-fullscreen')) {
        this.maximizedElement.remove();
      }
      
      this.maximizedElement = null;
    }
  },
  
  computed: {
    previewWrapperStyle() {
      //let height = Math.max(this.minHeightForPreview + 10, this.containerHeight - 50 || 0);
      const height =  this.containerHeight - 50;
      // Dynamic height based on minHeightForPreview + 10px
      return {
        height: `${height}px`
      };
    },
    
    isMobileView() {
      return this.windowWidth < this.mdBreakpoint;
    },
    isContainerTooSmall() {
      // Additional check for height
      if (this.containerHeight !== null && this.containerHeight < this.minHeightForPreview) {
        return true;
      }
      return false;
    },
    
    shouldShowCompactView() {
      // Comprehensive check for when to show compact view
      return !this.isMaximized && (this.isMobileView || this.isContainerTooSmall);
    },
    
    containerSizeInfo() {
      // Useful for debugging or showing size info
      return {
        width: this.containerWidth,
        height: this.containerHeight,
        isCompact: this.shouldShowCompactView,
        reason: this.isMobileView ? 'width' : (this.isContainerTooSmall ? 'height' : 'normal')
      };
    },
    highlightedCode() {
      if (!this.formattedResultData || typeof Prism === 'undefined') {
        return this.escapeHtml(this.formattedResultData || '');
      }
      
      const language = this.isCSV ? Prism.languages.csv : Prism.languages.json;
      const grammar = this.isCSV ? 'csv' : 'json';
      
      if (language) {
        try {
          return Prism.highlight(this.formattedResultData, language, grammar);
        } catch (e) {
          console.error('Prism highlighting failed:', e);
          return this.escapeHtml(this.formattedResultData);
        }
      }
      
      return this.escapeHtml(this.formattedResultData);
    },
    isCSV() {
      const contentType = this.resolvedQueryDefinition?.contentType || 'json';
      return contentType === 'csv' || contentType === 'xlsx';
    },
    
    dataInclude: {
      get() {
        if (this.isCSV) {
          return this.resolvedQueryDefinition && 
            VcListUtils.csvInclude(this.resolvedQueryDefinition.include);
        } else {
          // For JSON, return the first included section
          const sections = ['days', 'hours', 'minutes', 'current', 'events', 'alerts'];
          return sections.find(section => 
            VcListUtils.hasValueInList(this.resolvedQueryDefinition?.include, section)
          ) || 'days';
        }
      },
      set(newValue) {
        if (this.isCSV) {
          const oldValue = VcListUtils.csvInclude(this.resolvedQueryDefinition.include);
          if (oldValue) {
            this.resolvedQueryDefinition.include = VcListUtils.setValueInList(
              this.resolvedQueryDefinition.include,
              oldValue,
              false,
              false
            );
          }
        } else {
          // For JSON, clear all sections first
          const sections = ['days', 'hours', 'minutes', 'current', 'events', 'alerts'];
          sections.forEach(section => {
            this.resolvedQueryDefinition.include = VcListUtils.setValueInList(
              this.resolvedQueryDefinition.include,
              section,
              false,
              false
            );
          });
        }
        
        this.resolvedQueryDefinition.include = VcListUtils.setValueInList(
          this.resolvedQueryDefinition.include,
          newValue,
          true,
          false
        );
      }
    },
    
    hiddenLineCount() {
      if (!this.rawRecords || this.rawRecords.length < 7 || !this.costSaverMode) return 0;
      return this.rawRecords.length - 7;
    },
    
    formattedResultData() {
      console.log('Formatting result data for display');
      if (this.isCSV) {
        // CSV formatting
        if (!this.resultData || !this.costSaverMode) return this.resultData;
        const splitLines = this.resultData.split(/\r?\n/);
        return splitLines.slice(0, 7).join('\r\n');
      } else {
        // JSON formatting
        if (!this.parsedJson) return '';
        
        let displayData = this.parsedJson;
        
        // Truncate for preview mode
        if (this.costSaverMode && displayData.days && displayData.days.length > 1) {
          displayData = { ...displayData };
          displayData.days = displayData.days.slice(0, 1);
          if (displayData.days[0].hours && displayData.days[0].hours.length > 2) {
            displayData.days[0] = { ...displayData.days[0] };
            const mid = Math.floor(displayData.days[0].hours.length / 2);
            displayData.days[0].hours = displayData.days[0].hours.slice(mid, mid + 2);
          }
        }
        
        return JSON.stringify(displayData, null, 2);
      }
    },
    
    columns() {
      if (!this.rawRecords || !this.rawRecords[0]) return [];
      return this.rawRecords[0];
    },
    
    records() {
      if (!this.rawRecords) return [];
      return this.rawRecords.filter((d, i) => {
        if (i === 0) return false;
        if (this.costSaverMode && i > 7) return false;
        return true;
      });
    }
  },
  
  watch: {
    location: {
      handler(newValue, oldValue) {
        this.createResolvedQueryDefinition();
        this.scheduleRefresh();
      },
      deep: true
    },
    
    queryDefinition: {
      handler(newValue, oldValue) {
        // Only trigger if actually changed (not on initial mount)
       
        console.log('queryDefinition changed');
        this.createResolvedQueryDefinition();
      },
      deep: true
    },
    
    resolvedQueryDefinition: {
      handler(newValue, oldValue) {
        if (JSON.stringify(newValue) === JSON.stringify(oldValue)) {
          return;
        }
        console.log('resolvedQueryDefinition changed');
        this.scheduleRefresh();
      },
      deep: true
    },
    
    costSaverMode(newValue) {
      // Clear cache when switching modes as the data will be different
      this.clearCache();
      this.scheduleRefresh();
    },
  },
  
  mounted() {
    this.createResolvedQueryDefinition();

    // Set up resize observer
    this.$nextTick(() => {
      this.setupResizeObserver();
      // Only schedule refresh after everything is initialized
      //this.scheduleRefresh();
    });
    
    // Track window resize (as fallback)
    this.handleResize = () => {
      this.windowWidth = window.innerWidth;
    };
    window.addEventListener('resize', this.handleResize);

    // Clean up expired cache entries periodically
    this.cacheCleanupTimer = setInterval(() => {
        this.cleanupCache();
      }, 60000); // Clean up every minute
    },
  
  beforeUnmount() {
    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    // Clean up timers
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
    if (this.cacheCleanupTimer) {
      clearInterval(this.cacheCleanupTimer);
    }
    
    // Clear cache on unmount
    this.clearCache();

    // Remove resize listener
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
  }
};
</script>

<style scoped>

/* The wrapper establishes the height constraint */
.preview-wrapper {
  position: relative;
  /* height: 150px; /* Fixed height - adjust as needed */
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  background-color: #f8f9fa;
  overflow: hidden; /* Critical: hide overflow at wrapper level */
}

/* Pre takes full height and handles its own scrolling */
.preview-pre {
  position: absolute; /* Take it out of flow */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 0;
  padding: 0.5rem;
  background: transparent;
  border: none;
  overflow: auto; /* Scroll at pre level */
  max-width: 100%;
  box-sizing: border-box;
  
  /* Prevent horizontal expansion */
  white-space: pre;
  word-wrap: normal;
  overflow-wrap: normal;
}

/* Ensure code block doesn't cause overflow */
.preview-pre code {
  display: block;
  font-size: 0.75rem;
  line-height: 1.1;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  
  /* Don't break the pre formatting */
  white-space: inherit;
  word-wrap: inherit;
  overflow-wrap: inherit;
}

/* Alternative: If you need it to be flexible height with a max */
.preview-wrapper-flexible {
  position: relative;
  max-height: 180px; /* Maximum height */
  min-height: 100px; /* Minimum height */
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  background-color: #f8f9fa;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.preview-wrapper-flexible .preview-pre {
  position: relative; /* Normal flow */
  flex: 1 1 auto;
  min-height: 0; /* Critical for flexbox */
  margin: 0;
  padding: 1rem;
  overflow: auto;
}



/* Alert and other styles remain the same */
.alert {
  margin-bottom: 1rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.2em;
}

/* Syntax highlighting colors */
:deep(.language-json .token.property) {
  color: #905;
}

:deep(.language-json .token.string) {
  color: #690;
}

:deep(.language-json .token.boolean),
:deep(.language-json .token.number) {
  color: #905;
}

:deep(.language-json .token.null) {
  color: #708;
}

:deep(.language-csv .token.punctuation) {
  color: #6e7781;
}

:deep(.language-csv .token.string) {
  color: #032f62;
}
:deep(.language-csv .token.value) {
  color: #032f62;
}

/* Fullscreen overlay styles */
:global(.download-preview-fullscreen) {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  animation: fadeIn 0.2s ease-in;
}

:global(.download-preview-fullscreen .fullscreen-content) {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 95%;
  margin: 0 auto;
  padding: 20px;
}

:global(.download-preview-fullscreen .fullscreen-header) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  margin-bottom: 1rem;
}

:global(.download-preview-fullscreen .fullscreen-body) {
  flex: 1;
  overflow: hidden;
  background: #2d2d30;
  border-radius: 0.375rem;
  padding: 1rem;
}

:global(.download-preview-fullscreen .preview-pre) {
  height: 100%;
  margin: 0;
  background: transparent;
  border: none;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Maximized preview wrapper adjustments */
.preview-wrapper.maximized {
  transition: all 0.2s ease-in-out;
}

/* Responsive - make it smaller on mobile */
@media (max-width: 768px) {
  .preview-container,
  .table-responsive {
    max-height: 180px;
  }
  
  .preview-container code {
    font-size: 0.75rem;
  }
}

/* Mobile expand button styling */
.btn-link {
  font-size: 1rem;
  font-weight: 500;
}

.btn-link:hover {
  text-decoration: underline !important;
}

/* Ensure fullscreen works well on mobile */
@media (max-width: 767px) {
  :global(.download-preview-fullscreen .fullscreen-content) {
    padding: 10px;
    max-width: 100%;
  }
  
  :global(.download-preview-fullscreen .fullscreen-body) {
    padding: 0.5rem;
  }
  
  :global(.download-preview-fullscreen .preview-pre) {
    font-size: 0.7rem;
  }
}

/* Maximized container animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Ensure the maximized container is properly positioned */
:global(.download-preview-maximized-container) {
  animation: fadeIn 0.2s ease-in;
}

:global(.download-preview-maximized-container .preview-pre) {
  position: static !important;
  height: auto !important;
  max-height: none !important;
  overflow: auto !important;
  background: #f8f9fa;
}

</style>